// ===========================================
// Note Editor Component
// ===========================================
// Component for creating/editing class and student notes
// Notes are separated by visibility: Teacher Only, Parent Visible, Student Visible

import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Divider,
  Card,
  CardContent,
  CardActions,
  Collapse,
} from '@mui/material';
import {
  Save as SaveIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  Note,
  NoteVisibility,
  getNoteStatusColor,
  getNoteStatusLabel,
  isClassNote,
} from '../../api/notes.api';
import {
  useNotesByLesson,
  useLessonNoteCompletion,
  useCreateNote,
  useUpdateNote,
} from '../../hooks/useNotes';
import { useEnrolledStudentsForAttendance } from '../../hooks/useAttendance';

// ===========================================
// TYPES
// ===========================================

interface NoteEditorProps {
  lessonId: string;
  lessonName: string;
  date: Date;
  onClose?: () => void;
  onSaved?: () => void;
  // When true (hybrid individual sessions), the top-level "Class Notes" tab
  // is suppressed and the editor opens directly on the Student Notes tab.
  // The Student Notes tab's per-visibility cards (Teacher / Parent / Student)
  // remain active.
  hideClassNotesTab?: boolean;
  // When set, the enrolled-students lookup is narrowed to this one student
  // and the student dropdown is hidden — used alongside hideClassNotesTab
  // for hybrid individual sessions.
  studentId?: string;
}

type NoteType = 'class' | 'student';

interface NoteSection {
  visibility: NoteVisibility;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: 'error' | 'warning' | 'success';
}

// ===========================================
// CONSTANTS
// ===========================================

const CLASS_NOTE_SECTIONS: NoteSection[] = [
  {
    visibility: 'TEACHERS_ONLY',
    label: 'Teacher Notes',
    description: 'Private notes visible only to teachers',
    icon: <LockIcon />,
    color: 'error',
  },
  {
    visibility: 'PARENTS',
    label: 'Parent Notes',
    description: 'Notes visible to parents of enrolled students',
    icon: <VisibilityIcon />,
    color: 'warning',
  },
  {
    visibility: 'STUDENTS',
    label: 'Visible to Students',
    description: 'Notes visible to all students in the class',
    icon: <VisibilityIcon />,
    color: 'success',
  },
];

const STUDENT_NOTE_SECTIONS: NoteSection[] = [
  {
    visibility: 'TEACHERS_ONLY',
    label: 'Teacher Notes',
    description: 'Private notes visible only to teachers',
    icon: <LockIcon />,
    color: 'error',
  },
  {
    visibility: 'PARENTS',
    label: 'Parent Notes',
    description: 'Notes visible to the student\'s parents',
    icon: <VisibilityIcon />,
    color: 'warning',
  },
  {
    visibility: 'STUDENTS',
    label: 'Student Notes',
    description: 'Notes visible to the student (and their parents)',
    icon: <VisibilityIcon />,
    color: 'success',
  },
];

// ===========================================
// NOTE CARD COMPONENT
// ===========================================

interface NoteCardProps {
  section: NoteSection;
  existingNote?: Note;
  onSave: (content: string, visibility: NoteVisibility) => Promise<void>;
  isSaving: boolean;
}

function NoteCard({ section, existingNote, onSave, isSaving }: NoteCardProps) {
  const [isExpanded, setIsExpanded] = useState(!!existingNote);
  const [content, setContent] = useState(existingNote?.content || '');
  const [isDirty, setIsDirty] = useState(false);

  // Update content when existing note changes
  useEffect(() => {
    setContent(existingNote?.content || '');
    setIsDirty(false);
  }, [existingNote]);

  const handleContentChange = (value: string) => {
    setContent(value);
    setIsDirty(value !== (existingNote?.content || ''));
  };

  const handleSave = async () => {
    await onSave(content, section.visibility);
    setIsDirty(false);
  };

  const hasContent = content.trim().length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderColor: existingNote ? `${section.color}.main` : 'divider',
        borderWidth: existingNote ? 2 : 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {section.icon}
          <Box>
            <Typography variant="subtitle1" fontWeight="medium">
              {section.label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {section.description}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {existingNote ? (
            <Chip label="Added" size="small" color={section.color} />
          ) : (
            <Chip label="Not Added" size="small" variant="outlined" />
          )}
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Divider />
        <CardContent>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder={`Enter ${section.label.toLowerCase()}...`}
            value={content}
            onChange={(e) => handleContentChange(e.target.value)}
            helperText={`${content.length}/5000 characters`}
            inputProps={{ maxLength: 5000 }}
          />
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
          <Button
            variant="contained"
            color={section.color}
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasContent || isSaving || (!isDirty && !!existingNote)}
            size="small"
          >
            {isSaving ? 'Saving...' : existingNote ? 'Update' : 'Save'}
          </Button>
        </CardActions>
      </Collapse>
    </Card>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function NoteEditor({
  lessonId,
  lessonName,
  date,
  onClose,
  hideClassNotesTab,
  studentId: scopedStudentId,
}: NoteEditorProps) {
  const dateString = format(date, 'yyyy-MM-dd');

  // State — when the Class Notes tab is hidden, open straight on Student Notes.
  const [noteType, setNoteType] = useState<NoteType>(
    hideClassNotesTab ? 'student' : 'class'
  );
  // When scopedStudentId is provided, start with it pre-selected.
  const [selectedStudentId, setSelectedStudentId] = useState<string>(scopedStudentId ?? '');

  // Queries
  const { data: notes, isLoading: isLoadingNotes, refetch } = useNotesByLesson(lessonId, {
    date: dateString,
  });
  const { data: completion, isLoading: isLoadingCompletion } = useLessonNoteCompletion(
    lessonId,
    dateString,
    { skipClassNotes: hideClassNotesTab }
  );
  const { data: enrolledData, isLoading: isLoadingStudents } = useEnrolledStudentsForAttendance(
    lessonId,
    dateString,
    scopedStudentId
  );

  // Mutations
  const createNoteMutation = useCreateNote();
  const updateNoteMutation = useUpdateNote();

  // Memoized data
  const students = enrolledData?.students || [];

  // Auto-select the first (often only) student when the Student Notes tab is
  // the active starting tab — i.e. for hybrid individual sessions where the
  // Class Notes tab is hidden. The existing handleTabChange fallback only
  // covers user-initiated tab switches.
  useEffect(() => {
    if (noteType === 'student' && !selectedStudentId && students.length > 0) {
      setSelectedStudentId(students[0].id);
    }
  }, [noteType, selectedStudentId, students]);

  // Get class notes by visibility
  const classNotes = useMemo((): Record<NoteVisibility, Note | undefined> => {
    const allClassNotes = notes?.filter((n) => isClassNote(n)) || [];
    return {
      TEACHERS_ONLY: allClassNotes.find((n) => n.visibility === 'TEACHERS_ONLY'),
      PARENTS: allClassNotes.find((n) => n.visibility === 'PARENTS'),
      STUDENTS: allClassNotes.find((n) => n.visibility === 'STUDENTS'),
    };
  }, [notes]);

  // Get student notes for selected student by visibility
  const studentNotes = useMemo((): Record<NoteVisibility, Note | undefined> => {
    if (!selectedStudentId) return { TEACHERS_ONLY: undefined, PARENTS: undefined, STUDENTS: undefined };
    const allStudentNotes = notes?.filter((n) => n.studentId === selectedStudentId) || [];
    return {
      TEACHERS_ONLY: allStudentNotes.find((n) => n.visibility === 'TEACHERS_ONLY'),
      PARENTS: allStudentNotes.find((n) => n.visibility === 'PARENTS'),
      STUDENTS: allStudentNotes.find((n) => n.visibility === 'STUDENTS'),
    };
  }, [notes, selectedStudentId]);

  // Count students with notes. When the editor is scoped to a single student
  // (hybrid individual sessions), exclude notes belonging to other concurrent
  // bookings on the same lessonId+date so the chip reads e.g. 1/1 not 2/1.
  const studentsWithNotes = useMemo(() => {
    const studentIds = new Set(
      notes
        ?.filter(
          (n) => n.studentId && (!scopedStudentId || n.studentId === scopedStudentId)
        )
        .map((n) => n.studentId)
    );
    return studentIds.size;
  }, [notes, scopedStudentId]);

  // Handle tab change
  const handleTabChange = (_: React.SyntheticEvent, newValue: NoteType) => {
    setNoteType(newValue);
    if (newValue === 'student' && students.length > 0 && !selectedStudentId) {
      setSelectedStudentId(students[0].id);
    }
  };

  // Handle student selection
  const handleStudentChange = (event: SelectChangeEvent) => {
    setSelectedStudentId(event.target.value);
  };

  // Check if student has any note
  const hasStudentNote = (studentId: string) => {
    return notes?.some((n) => n.studentId === studentId);
  };

  // Handle save for class notes
  const handleSaveClassNote = async (content: string, visibility: NoteVisibility) => {
    const existingNote = classNotes[visibility];

    if (existingNote) {
      await updateNoteMutation.mutateAsync({
        id: existingNote.id,
        data: { content },
      });
    } else {
      await createNoteMutation.mutateAsync({
        lessonId,
        date: dateString,
        content,
        visibility,
      });
    }

    refetch();
  };

  // Handle save for student notes
  const handleSaveStudentNote = async (content: string, visibility: NoteVisibility) => {
    if (!selectedStudentId) return;

    const existingNote = studentNotes[visibility];

    if (existingNote) {
      await updateNoteMutation.mutateAsync({
        id: existingNote.id,
        data: { content },
      });
    } else {
      await createNoteMutation.mutateAsync({
        lessonId,
        studentId: selectedStudentId,
        date: dateString,
        content,
        visibility,
      });
    }

    refetch();
  };

  const isLoading = isLoadingNotes || isLoadingCompletion || isLoadingStudents;
  const isSaving = createNoteMutation.isPending || updateNoteMutation.isPending;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Teacher Notes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {lessonName} - {format(date, 'EEEE, d MMMM yyyy')}
        </Typography>
      </Box>

      {/* Completion Status */}
      {completion && (
        <Alert
          severity={completion.status === 'COMPLETE' ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          action={
            <Chip
              label={getNoteStatusLabel(completion.status)}
              color={getNoteStatusColor(completion.status)}
              size="small"
            />
          }
        >
          {completion.status === 'COMPLETE' ? (
            'All notes complete!'
          ) : (
            <>
              {!hideClassNotesTab && !completion.classNoteComplete && 'Class note required. '}
              {completion.missingStudentNotes.length > 0 &&
                `${completion.missingStudentNotes.length} student note(s) missing.`}
            </>
          )}
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* Note Type Tabs — Class Notes hidden for hybrid individual sessions */}
      <Tabs value={noteType} onChange={handleTabChange} sx={{ mb: 3 }}>
        {!hideClassNotesTab && (
          <Tab
            icon={<SchoolIcon />}
            iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Class Notes
                {(classNotes.TEACHERS_ONLY || classNotes.PARENTS || classNotes.STUDENTS) && (
                  <Chip label="Added" size="small" color="success" />
                )}
              </Box>
            }
            value="class"
          />
        )}
        <Tab
          icon={<PersonIcon />}
          iconPosition="start"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Student Notes
              <Chip
                label={`${studentsWithNotes}/${students.length}`}
                size="small"
                color={studentsWithNotes === students.length && students.length > 0 ? 'success' : 'warning'}
              />
            </Box>
          }
          value="student"
        />
      </Tabs>

      {/* Class Notes */}
      {noteType === 'class' && (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Add notes about the class session. Choose the appropriate visibility level for each note.
          </Typography>

          {CLASS_NOTE_SECTIONS.map((section) => (
            <NoteCard
              key={section.visibility}
              section={section}
              existingNote={classNotes[section.visibility]}
              onSave={handleSaveClassNote}
              isSaving={isSaving}
            />
          ))}
        </Box>
      )}

      {/* Student Notes */}
      {noteType === 'student' && (
        <Box>
          {/* Student Selector — hidden when the editor is scoped to a single
              student (hybrid individual sessions). */}
          {!scopedStudentId && (
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Student</InputLabel>
              <Select
                value={selectedStudentId}
                onChange={handleStudentChange}
                label="Select Student"
              >
                {students.map((student) => (
                  <MenuItem key={student.id} value={student.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography>
                        {student.firstName} {student.lastName}
                      </Typography>
                      {hasStudentNote(student.id) ? (
                        <Chip label="Note Added" size="small" color="success" sx={{ ml: 'auto' }} />
                      ) : (
                        <Chip label="No Note" size="small" color="warning" sx={{ ml: 'auto' }} />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {selectedStudentId ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add notes for this student. Choose the appropriate visibility level for each note.
              </Typography>

              {STUDENT_NOTE_SECTIONS.map((section) => (
                <NoteCard
                  key={section.visibility}
                  section={section}
                  existingNote={studentNotes[section.visibility]}
                  onSave={handleSaveStudentNote}
                  isSaving={isSaving}
                />
              ))}
            </>
          ) : (
            <Alert severity="info">Select a student to add notes.</Alert>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        {onClose && (
          <Button variant="outlined" onClick={onClose}>
            Close
          </Button>
        )}
      </Box>
    </Paper>
  );
}
